import React, { useState } from 'react';
import ClinicLayout from './ClinicLayout';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const ClinicAppointments = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before the first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(i);
    }
    
    return days;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <ClinicLayout activeTab="appointments">
      <div className="p-4 pr-20" data-testid="clinic-appointments">
        {/* Sub Navigation Tabs */}
        <div className="flex items-center border-b border-gray-300 mb-4">
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-white -mb-px">Add New Appointment</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Search</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Daily View</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Weekly View</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Monthly View</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Options</button>
          <button className="px-3 py-2 text-xs border border-gray-300 border-b-0 bg-gray-200">Reports</button>
        </div>

        <div className="flex gap-4">
          {/* Left Column - Calendar */}
          <div className="w-64">
            {/* Calendar Widget */}
            <div className="border border-gray-300 bg-white">
              <div className="bg-[#1e3a5f] text-white px-2 py-1 flex items-center justify-between">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="p-1">
                <div className="grid grid-cols-7 text-xs text-center">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                    <div key={day} className="p-1 font-medium text-gray-600">{day}</div>
                  ))}
                  {getDaysInMonth(currentMonth).map((day, idx) => (
                    <div 
                      key={idx} 
                      className={`p-1 cursor-pointer hover:bg-blue-100 ${
                        day === selectedDate.getDate() && 
                        currentMonth.getMonth() === selectedDate.getMonth() 
                          ? 'bg-[#1e3a5f] text-white' 
                          : ''
                      }`}
                      onClick={() => day && setSelectedDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
                    >
                      {day}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Office/Provider Selection */}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <label className="w-16">Office:</label>
                <select className="flex-1 border border-gray-400 px-1 py-0.5">
                  <option>Office1</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-16">Providers:</label>
                <select className="flex-1 border border-gray-400 px-1 py-0.5">
                  <option>-- All --</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-16">Staff:</label>
                <select className="flex-1 border border-gray-400 px-1 py-0.5">
                  <option>-- All --</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-16">Time Interval:</label>
                <select className="flex-1 border border-gray-400 px-1 py-0.5">
                  <option>15 min</option>
                  <option>30 min</option>
                </select>
              </div>
            </div>
          </div>

          {/* Right Column - Schedule View */}
          <div className="flex-1">
            <div className="border border-gray-300">
              {/* Date Header */}
              <div className="bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-white px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button><ChevronLeft className="w-4 h-4" /></button>
                  <span className="text-sm font-medium">{formatDate(selectedDate)}</span>
                  <button><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-500 text-sm font-bold">CLOSED</span>
                </div>
              </div>

              {/* Schedule Grid */}
              <div className="bg-[#e8f4e8] min-h-[300px] flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <p className="text-lg">-- CLOSED --</p>
                  <p className="text-sm mt-2">No appointments scheduled for this day</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClinicLayout>
  );
};

export default ClinicAppointments;
